import React, { useEffect, useState, useContext, useMemo, useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import { useSelector, useDispatch } from 'react-redux';
import {
  Drawer,
  Avatar,
  Box,
  IconButton,
  Typography,
  Divider,
  Fade,
  Slide,
  Collapse,
  Badge,
  styled,
  keyframes,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as icon from '@fortawesome/free-solid-svg-icons';
import ReactHtmlParser from 'html-react-parser';
import {
  faFacebook,
  faInstagram,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// Context e Actions
import SystemContext from '../../../context/SystemContext';
import * as kApi from '../../../constants/api';
import { setSessionActiveUser } from '../../../store/actions/_LocalSession';
import * as routes from '../../../constants/routes';
import * as analytics from '../../../helpers/analyticsHelpers';
import * as actions from '../../../store/actions';
import * as helpers from '../../../helpers/index';
import { setAnalyticsNotifyClick } from '../../../helpers/analyticsHelpers';
import { PROBRAIN_LINK } from '../../../constants/api';

// Assets
import emptyImg from '../../../assets/images/SVG/afinandinho_empty.svg';
import AvatarManager from '../../Avatar/AvatarManager';

// ============================================================================
// STYLED COMPONENTS - Sistema de Design MUI
// ============================================================================

const PRIMARY_COLOR = '#662d91';
const SECONDARY_COLOR = '#662d91';
const GRAY_DARKER = '#333333';
const BACKGROUND_COLOR = '#fafafa';
const DRAWER_WIDTH = 300;

// Animação de "jello" para o badge de notificações
const jelloAnimation = keyframes`
  0%, 76% {
    transform: scale3d(1, 1, 1);
  }
  80% {
    transform: scale3d(1.25, 0.75, 1);
  }
  84% {
    transform: scale3d(0.75, 1.25, 1);
  }
  86% {
    transform: scale3d(1.15, 0.85, 1);
  }
  88% {
    transform: scale3d(0.95, 1.05, 1);
  }
  90% {
    transform: scale3d(1.05, 0.95, 1);
  }
  92%, 100% {
    transform: scale3d(1, 1, 1);
  }
`;

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    // Em mobile e tablet, ocupa toda a largura da tela
    // Em desktop, mantém a largura fixa de 300px
    width: '100%',
    maxWidth: DRAWER_WIDTH,
    backgroundColor: BACKGROUND_COLOR,
    borderRadius: '10px 0 0 0',
    overflowX: 'hidden',
    // Garante que o drawer fique sobre todo o conteúdo
    zIndex: theme.zIndex.drawer + 2,
    // Em telas pequenas (mobile/tablet), remove a borda arredondada
    // pois o menu ocupa a tela toda
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      borderRadius: 0,
    },
  },
}));

const NotificationDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    // Em mobile e tablet, ocupa toda a largura da tela
    // Em desktop, mantém a largura fixa de 300px
    width: '100%',
    maxWidth: DRAWER_WIDTH,
    backgroundColor: '#ffffff',
    overflowX: 'hidden',
    // Garante que o drawer de notificações fique sobre o menu principal
    zIndex: theme.zIndex.drawer + 3,
    // Em telas pequenas, remove bordas arredondadas
    [theme.breakpoints.down('md')]: {
      maxWidth: '100%',
      borderRadius: 0,
    },
  },
}));

const Overlay = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.8)',
  zIndex: 100,
}));

const MenuContainer = styled(Box)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const AvatarSection = styled(Box)({
  textAlign: 'center',
  padding: '10px',
  marginTop: '30px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '10px',
});

const StyledAvatar = styled(Avatar)({
  backgroundColor: PRIMARY_COLOR,
  width: 56,
  height: 56,
});

const UserName = styled(Typography)({
  fontSize: '1.2rem',
  color: GRAY_DARKER,
  fontWeight: 500,
});

const MyAccountButton = styled(Box)({
  marginTop: '20px',
  marginBottom: '30px',
  color: GRAY_DARKER,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
});

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '0 10px',
});

const BottomContainer = styled(Box)({
  width: '100%',
  padding: '10px 0',
  display: 'flex',
  justifyContent: 'center',
  position: 'sticky',
  bottom: 0,
  backgroundColor: BACKGROUND_COLOR,
});

// AJUSTE: Border-radius alterado de 50px para 12px para um visual mais quadrado
const ActionButton = styled(Box)(({ variant = 'primary' }) => ({
  borderRadius: '12px', // Alterado de 50px para 12px
  border: `2px solid ${PRIMARY_COLOR}`,
  backgroundColor: variant === 'primary' ? BACKGROUND_COLOR : '#ffffff',
  padding: '12px 24px',
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  minWidth: '200px',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const SocialIconsContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  padding: '20px',
  marginTop: '20px',
  gap: '20px',
});

const SocialIconLink = styled('a')({
  height: '50px',
  width: '50px',
  borderRadius: '50%',
  border: `2px solid ${SECONDARY_COLOR}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: SECONDARY_COLOR,
  transition: 'all 0.3s ease',
  textDecoration: 'none',
  '&:hover': {
    transform: 'scale(1.1)',
    backgroundColor: PRIMARY_COLOR,
    color: '#ffffff',
  },
});

const LineDivider = styled(Box)({
  width: '100%',
  height: '2px',
  margin: '15px 0',
  position: 'relative',
  overflow: 'hidden',
});

const AnimatedLine = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOpen'
})(({ isOpen }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: PRIMARY_COLOR,
  transform: isOpen ? 'scaleX(1)' : 'scaleX(0)',
  transformOrigin: 'left',
  transition: 'transform 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionDelay: isOpen ? '0.5s' : '0s',
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#cb415b',
    color: '#ffffff',
    animation: `${jelloAnimation} 3.5s infinite both`,
    right: -3,
    top: 3,
    padding: '0 4px',
    height: '15px',
    minWidth: '16px',
    fontSize: '0.7rem',
  },
}));

const EmptyStateContainer = styled(Box)({
  height: '80vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  gap: '20px',
  '& h1': {
    color: PRIMARY_COLOR,
  },
  '& img': {
    maxWidth: '200px',
    opacity: 0.7,
  },
});

const NotificationCard = styled(Box)({
  border: `1px solid ${PRIMARY_COLOR}40`,
  borderRadius: '5px',
  padding: '10px',
  marginTop: '10px',
  marginRight: '1px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'translateX(-5px)',
    boxShadow: '0 2px 8px rgba(102, 45, 145, 0.2)',
  },
});

const CloseButtonContainer = styled(Box)({
  position: 'absolute',
  right: 10,
  top: 10,
  zIndex: 10,
});

const MenuButton = styled(IconButton)(({ theme }) => ({
  color: PRIMARY_COLOR,
  backgroundColor: BACKGROUND_COLOR,
  border: `2px solid ${PRIMARY_COLOR}`,
  padding: '8px',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: PRIMARY_COLOR,
    color: '#ffffff',
    transform: 'scale(1.1)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

const FloatingMenuButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  top: 20,
  right: 20,
  zIndex: 99,
  color: '#ffffff',
  backgroundColor: PRIMARY_COLOR,
  border: `2px solid ${PRIMARY_COLOR}`,
  padding: '12px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#7d3ba8',
    transform: 'scale(1.1)',
    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
  },
  '&:active': {
    transform: 'scale(0.95)',
  },
}));

// Styled components para os itens de menu - ESPAÇAMENTO REDUZIDO
const MenuList = styled(List)({
  padding: '5px 0', // Reduzido de 10px para 5px
  '& .MuiListItem-root': {
    padding: '2px 8px', // Reduzido de 4px para 2px
  },
});

const MenuItemButton = styled(ListItemButton)({
  borderRadius: '8px',
  margin: '2px 0', // Reduzido de 4px para 2px
  padding: '8px 8px', // Reduzido de 12px para 8px
  transition: 'all 0.2s ease',
  color: SECONDARY_COLOR,
  '&:hover': {
    backgroundColor: `${PRIMARY_COLOR}10`,
    transform: 'translateX(5px)',
  },
  '&:active': {
    transform: 'scale(0.98)',
  },
});

const MenuItemText = styled(ListItemText)({
  '& .MuiTypography-root': {
    fontSize: '1rem',
    fontWeight: 500,
    color: SECONDARY_COLOR,
  },
});

const DropdownButton = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 16px', // Reduzido de 12px para 8px
  margin: '2px 0', // Reduzido de 4px para 2px
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  color: SECONDARY_COLOR,
  '&:hover': {
    backgroundColor: `${PRIMARY_COLOR}10`,
  },
});

// ============================================================================
// HOOK PERSONALIZADO - Local Storage
// ============================================================================

const useLocalStorage = (key, initialValue) => {
  const [storedValue, setStoredValue] = useState(() => {
    if (typeof window === 'undefined') {
      return initialValue;
    }
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue];
};

// ============================================================================
// COMPONENTES INTERNOS
// ============================================================================

/**
 * Componente de ícones sociais
 * Exibe links para redes sociais da aplicação
 */
const SocialIcons = () => (
  <SocialIconsContainer>
    <SocialIconLink
      href="https://www.facebook.com/afinandoocerebro/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Facebook"
    >
      <FontAwesomeIcon icon={faFacebook} size="lg" />
    </SocialIconLink>
    <SocialIconLink
      href="https://www.instagram.com/afinandoocerebro/"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram"
    >
      <FontAwesomeIcon icon={faInstagram} size="lg" />
    </SocialIconLink>
    <SocialIconLink
      href="https://www.youtube.com/channel/UCJ7wZtwP9IOV5nM2ap1D9_w"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="YouTube"
    >
      <FontAwesomeIcon icon={faYoutube} size="lg" />
    </SocialIconLink>
  </SocialIconsContainer>
);

/**
 * Componente de estado vazio para notificações
 */
const EmptyNotificationState = () => (
  <EmptyStateContainer>
    <img src={emptyImg} alt="Sem notificações" />
    <Typography variant="h6" color="textSecondary">
      Nenhuma notificação disponível
    </Typography>
  </EmptyStateContainer>
);

/**
 * Componente de navegação do menu
 * Renderiza todos os itens do menu de forma inline
 */
const MenuNavigation = ({
  hasParent,
  onNavigate,
  onToggleNotify,
  onTourGuide,
  onLogout,
  notifyCount,
  t
}) => {
  const [isPoliciesOpen, setIsPoliciesOpen] = useState(false);
  const [isOtherProductsOpen, setIsOtherProductsOpen] = useState(false);
  const { session } = useSelector((state) => state.session);


  const handleNavigation = (path) => {
    onNavigate(path);
  };

  const handleExternalLink = (url) => {
    window.open(url, '_blank');
  };

  const isPatient = session?.my_user?.typeProfile?.includes("patient");
  const isProfessional = session?.my_user?.typeProfile?.includes("professional");

  return (
    <Box sx={{ px: 1 }}>
      {/* Seção Superior - Navegação Principal */}
      <MenuList>
        {/* Notificações - ÍCONE DE SINO */}
        <Fade in timeout={300}>
          <ListItem disablePadding>
            <MenuItemButton onClick={onToggleNotify}>
              <MenuItemText primary={t?.('navs.notify') || 'Notificações'} />
              {notifyCount > 0 && (
                <Badge badgeContent={notifyCount} color="error" sx={{ ml: 1 }} />
              )}
              <FontAwesomeIcon
                icon={icon.faBell}
                style={{ marginLeft: '8px', fontSize: '1rem' }}
              />
            </MenuItemButton>
          </ListItem>
        </Fade>

        {/* Convidar Usuário */}
        {!isPatient &&
          <Fade in timeout={350}>
            <ListItem disablePadding>
              <MenuItemButton onClick={() => handleNavigation(routes.R_INVITE_USER)}>
                <MenuItemText primary={t?.('global.invite_user') || 'Convidar usuário'} />
              </MenuItemButton>
            </ListItem>
          </Fade>
        }

        {/* Resgatar Código / Desvincular Perfil */}
        {!isProfessional &&
          <Fade in timeout={400}>
            <ListItem disablePadding>
              <MenuItemButton onClick={() => handleNavigation(routes.R_RESCUE_CODE)}>
                <MenuItemText
                  primary={
                    hasParent
                      ? t?.('global.unlink_profile') || 'Desvincular perfil'
                      : t?.('navs.rescue_invitation_code') || 'Resgatar código de convite'
                  }
                />
              </MenuItemButton>
            </ListItem>
          </Fade>
        }

        {/* Gerenciar Usuários */}
        {!isPatient &&
          <Fade in timeout={450}>
            <ListItem disablePadding>
              <MenuItemButton onClick={() => handleNavigation(routes.R_LIST_MY_USERS)}>
                <MenuItemText primary={t?.('global.manage_users') || 'Gerenciar usuários'} />
              </MenuItemButton>
            </ListItem>
          </Fade>
        }

        {/* Tour Guiado */}
        <Fade in timeout={500}>
          <ListItem disablePadding>
            <MenuItemButton onClick={onTourGuide}>
              <MenuItemText primary={t?.('Tour Guiado') || 'Tour Guiado'} />
            </MenuItemButton>
          </ListItem>
        </Fade>

        {/* Conteúdos */}
        <Fade in timeout={550}>
          <ListItem disablePadding>
            <MenuItemButton
              onClick={() => handleExternalLink('https://www.afinandocerebro.com.br/blog')}
            >
              <MenuItemText primary={t?.('navs.contents') || 'Conteúdos'} />
              <FontAwesomeIcon
                icon={icon.faExternalLinkAlt}
                style={{ marginLeft: '8px', fontSize: '0.8rem' }}
              />
            </MenuItemButton>
          </ListItem>
        </Fade>

        {/* Outros produtos */}
        <Fade in timeout={700}>
          <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <DropdownButton onClick={() => setIsOtherProductsOpen(!isOtherProductsOpen)}>
              <Typography sx={{ fontWeight: 500, color: SECONDARY_COLOR }}>
                Outros Produtos
              </Typography>
              <FontAwesomeIcon
                icon={isOtherProductsOpen ? icon.faChevronUp : icon.faChevronDown}
                style={{ fontSize: '0.9rem' }}
              />
            </DropdownButton>

            {/* Subitens de outros produtos */}
            <Collapse in={isOtherProductsOpen} timeout={300}>
              <Box sx={{ pl: 2, mt: 1 }}>
                <MenuItemButton
                  href="https://audiofoco.probrain.com.br/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir página do Audiofoco"
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    - {t?.('global.audiofoco') || 'Audiofoco'}
                  </Typography>
                </MenuItemButton>

                <MenuItemButton
                  href="https://www.audbility.com.br/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir página do Audbility"
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    - {t?.('global.audbility') || 'Audbility'}
                  </Typography>
                </MenuItemButton>

                <MenuItemButton
                  href="https://afinando.probrain.com.br/probrain-todos-cursos"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Abrir página de Cursos EAD"
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    - {t?.('global.distance_courses') || 'Cursos EAD'}
                  </Typography>
                </MenuItemButton>
              </Box>
            </Collapse>
          </ListItem>
        </Fade>

      </MenuList>

      {/* Divider */}
      <LineDivider>
        <AnimatedLine isOpen={true} />
      </LineDivider>

      {/* Seção Média - Conta e Termos */}
      <MenuList>
        {/* Gerenciar Minha Conta */}
        <Fade in timeout={600}>
          <ListItem disablePadding>
            <MenuItemButton onClick={() => handleNavigation(routes.R_MY_ACCOUNT)}>
              <MenuItemText primary={t?.('global.manage_my_account') || 'Gerenciar minha conta'} />
            </MenuItemButton>
          </ListItem>
        </Fade>

        {/* Termos de Uso */}
        <Fade in timeout={650}>
          <ListItem disablePadding>
            <MenuItemButton
              onClick={() => handleNavigation(routes.GET_LEGAL_INFO('termos-de-uso'))}
            >
              <MenuItemText primary={t?.('global.terms_of_use') || 'Termos de uso'} />
            </MenuItemButton>
          </ListItem>
        </Fade>
      </MenuList>

      {/* Divider */}
      <LineDivider>
        <AnimatedLine isOpen={true} />
      </LineDivider>

      {/* Seção Inferior - Políticas e Ajuda */}
      <MenuList>
        {/* Políticas - Dropdown */}
        <Fade in timeout={700}>
          <ListItem disablePadding sx={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <DropdownButton onClick={() => setIsPoliciesOpen(!isPoliciesOpen)}>
              <Typography sx={{ fontWeight: 500, color: SECONDARY_COLOR }}>
                Políticas
              </Typography>
              <FontAwesomeIcon
                icon={isPoliciesOpen ? icon.faChevronUp : icon.faChevronDown}
                style={{ fontSize: '0.9rem' }}
              />
            </DropdownButton>

            {/* Subitens de Políticas */}
            <Collapse in={isPoliciesOpen} timeout={300}>
              <Box sx={{ pl: 2, mt: 1 }}>
                <MenuItemButton
                  onClick={() => handleNavigation(routes.GET_LEGAL_INFO('politica-de-seguranca'))}
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    - {t?.('global.security_policy') || 'Política de Segurança'}
                  </Typography>
                </MenuItemButton>

                <MenuItemButton
                  onClick={() => handleNavigation(routes.GET_LEGAL_INFO('politica-de-privacidade'))}
                  sx={{ py: 1 }}
                >
                  <Typography sx={{ fontSize: '0.9rem' }}>
                    - {t?.('global.terms_of_privacy') || 'Política de Privacidade'}
                  </Typography>
                </MenuItemButton>
              </Box>
            </Collapse>
          </ListItem>
        </Fade>

        {/* Ajuda / Falar Conosco */}
        <Fade in timeout={750}>
          <ListItem disablePadding>
            <MenuItemButton
              onClick={() => handleExternalLink('https://afinandoocerebro.zohodesk.com/portal/pt-br/kb')}
            >
              <MenuItemText primary={t?.('navs.talk_with_us') || 'Falar conosco'} />
              <FontAwesomeIcon
                icon={icon.faExternalLinkAlt}
                style={{ marginLeft: '8px', fontSize: '0.8rem' }}
              />
            </MenuItemButton>
          </ListItem>
        </Fade>
      </MenuList>

      {/* Divider final antes do botão Sair */}
      <LineDivider>
        <AnimatedLine isOpen={true} />
      </LineDivider>

      {/* Botão de Logout integrado ao menu */}
      <Box sx={{ px: 2, py: 2 }}>
        <Fade in timeout={800}>
          <ActionButton
            onClick={onLogout}
            role="button"
            tabIndex={0}
            sx={{ width: '100%' }}
          >
            <LogoutIcon sx={{ color: SECONDARY_COLOR }} />
            <Typography sx={{ color: SECONDARY_COLOR }}>
              {t?.('global.signout') || 'Sair'}
            </Typography>
          </ActionButton>
        </Fade>
      </Box>
    </Box>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const MotionMenu = ({
  isOpen,
  isOpenNotify,
  toggleOpen,
  notification,
  permissions,
  toggleOpenNotify,
}) => {
  // ============================================================================
  // HOOKS E STATE
  // ============================================================================

  const { session } = useSelector((state) => state.session);
  const my_user = session?.my_user || null;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setScrollable, setOpenModalStart } = useContext(SystemContext) || {};

  // Estados locais
  const [fcmNotification, setFcmNotification] = useState(null);
  const [fcmArray, setFcmArray] = useState([]);
  const [notifyPermission, setNotifyPermission] = useState(null);
  const [newNotify, setNewNotify] = useLocalStorage('newNotify', {
    status: false,
    quantity: 0,
  });

  // Flags e estados derivados
  const hasParent = !!session?.my_user?.parent_sub;

  // ============================================================================
  // FUNÇÕES AUXILIARES
  // ============================================================================

  /**
   * Reseta os estados de navegação e fecha os menus
   */
  const resetNavs = useCallback(() => {
    toggleOpen();
    if (isOpenNotify) {
      toggleOpenNotify();
    }
    setScrollable?.(true);
  }, [isOpenNotify, toggleOpen, toggleOpenNotify, setScrollable]);

  /**
   * Normaliza as notificações FCM recebidas
   * @param {Object} data - Dados da notificação FCM
   */
  const normalizeFcmNotification = useCallback((data) => {
    const hours = data.data?.durationHours || 72;
    const expirationDuration = moment.duration(hours, 'hours');

    const normalizedNotification = {
      analytics: data.data?.['google.c.a.m_l'],
      id: data.messageId,
      message: `
        <h4 style='margin-bottom:5px'>${data.notification.title}</h4>
        <img src="${data.notification.image}" width="200" height="130" alt="notification" />
        <p>${data.notification.body}</p>
      `,
      permission: ['all'],
      type: 'Url',
      call: data.data?.click_action,
      expires: moment().add(expirationDuration).toISOString(),
    };

    setFcmArray((prev) => [...prev, normalizedNotification]);
  }, []);

  /**
   * Importa todas as imagens de um contexto require
   * @param {Function} r - Contexto do require
   * @returns {Array} Array de imagens importadas
   */
  const importAll = useCallback((r) => r.keys().map(r), []);

  /**
   * Obtém uma imagem específica pelo nome
   * @param {string} name - Nome da imagem
   * @param {number} type - Tipo de renderização (0 = ícone, 1 = thumbnail)
   * @returns {JSX.Element} Elemento de imagem
   */
  const getImage = useCallback((name, type = 0) => {
    try {
      const images = importAll(
        require.context('../../../assets/images/icons', false, /\.(png|jpe?g|svg)$/)
      );

      const pattern = /\/|\./;
      const imgFile = images.find((element) => {
        const parts = element.default.split(pattern);
        return parts[3] === name;
      });

      if (!imgFile) return null;

      return type === 1 ? (
        <img src={imgFile.default} alt={name} style={{ maxHeight: '100px' }} />
      ) : (
        <img
          src={imgFile.default}
          alt={name}
          style={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: -10,
            margin: 'auto',
            transform: 'translateY(-50%)',
            width: '40px',
            height: '40px',
          }}
        />
      );
    } catch (error) {
      console.error('Error loading image:', error);
      return null;
    }
  }, [importAll]);

  /**
   * Verifica e filtra notificações com base nas permissões do usuário
   * @returns {Array} Array de notificações filtradas
   */
  const checkNotifyPermission = useCallback(() => {
    if (!notification || !notifyPermission) return [];

    let filteredNotifications = notification
      .filter((item) =>
        notifyPermission.some((permission) =>
          item.permission.includes(permission)
        )
      )
      .filter(Boolean);

    // Adiciona notificações FCM
    if (fcmArray.length > 0) {
      filteredNotifications = [...filteredNotifications, ...fcmArray];
    }

    // Atualiza o estado de novas notificações
    if (filteredNotifications.length > newNotify.quantity) {
      setNewNotify({ status: true, quantity: filteredNotifications.length });
    } else if (filteredNotifications.length < newNotify.quantity) {
      setNewNotify({ status: false, quantity: filteredNotifications.length });
    }

    // Envia notificações para o Electron (se disponível)
    try {
      const { ipcRenderer } = window.require('electron');
      if (ipcRenderer) {
        filteredNotifications.forEach((n) => {
          ipcRenderer.send(
            'showNotification',
            'Afinando o Cérebro - Notificação',
            n.message
          );
        });
      }
    } catch (error) {
      // Electron não disponível - executando no browser
    }

    return filteredNotifications;
  }, [notification, notifyPermission, fcmArray, newNotify, setNewNotify]);

  /**
   * Executa uma ação customizada baseada no tipo
   * @param {string} type - Tipo da ação (Function, Url, etc)
   * @param {string} call - Função ou URL a ser chamada
   * @param {Object} item - Item da notificação para analytics
   */
  const customCall = useCallback((type, call, item) => {
    setAnalyticsNotifyClick({ notify: item.analytics });

    switch (type) {
      case 'Function':
        // Aqui você pode adicionar suas funções customizadas
        console.log('Function call:', call);
        break;
      case 'Url':
        window.open(call, '_blank')?.focus();
        break;
      default:
        break;
    }
  }, []);

  /**
   * Realiza o logout do usuário
   */
  const handleLogout = useCallback(async () => {
    try {
      resetNavs();
      localStorage.setItem('google_sign_in', '0');
      await Auth.signOut();
      dispatch(actions.initLogout());
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }, [resetNavs, dispatch]);

  /**
   * Navega para a página de conta do usuário
   */
  const handleMyAccountClick = useCallback(() => {
    navigate(routes.R_MY_ACCOUNT);
    resetNavs();
  }, [navigate, resetNavs]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Controla o scroll do body quando o menu está aberto
   */
  useEffect(() => {
    if (isOpen) {
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.documentElement.style.overflow = 'auto';
    }

    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [isOpen]);

  /**
   * Registra listener para notificações FCM via Service Worker
   */
  useEffect(() => {
    if (!navigator.serviceWorker) return;

    const handleMessage = (event) => {
      if (event.data?.fcmMessageId) {
        setFcmNotification(event.data);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);

    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, []);

  /**
   * Processa notificação FCM quando recebida
   */
  useEffect(() => {
    if (fcmNotification) {
      normalizeFcmNotification(fcmNotification);
    }
  }, [fcmNotification, normalizeFcmNotification]);

  /**
   * Carrega notificações FCM do localStorage na inicialização
   */
  useEffect(() => {
    try {
      const storedNotifications = window.localStorage.getItem('fcmNotifications');
      if (storedNotifications) {
        setFcmArray(JSON.parse(storedNotifications));
      }
    } catch (error) {
      console.error('Error loading FCM notifications from localStorage:', error);
    }
  }, []);

  /**
   * Persiste notificações FCM no localStorage quando alteradas
   */
  useEffect(() => {
    if (fcmArray.length > 0) {
      try {
        window.localStorage.setItem('fcmNotifications', JSON.stringify(fcmArray));
      } catch (error) {
        console.error('Error saving FCM notifications to localStorage:', error);
      }
    }
  }, [fcmArray]);

  /**
   * Configura permissões de notificação baseadas no usuário e permissões do jogo
   */
  useEffect(() => {
    if (!my_user || !permissions) return;

    try {
      const userPermissions = new Set([
        ...permissions.game,
        my_user.profession,
        'all',
        my_user.typeUser,
      ]);

      // Ajusta permissão 'free' baseado na existência de parent
      const permissionsArray = Array.from(userPermissions).map((perm) => {
        if (perm === 'free') {
          return my_user.parent_sub ? 'freehasParent' : 'freenoParent';
        }
        return perm;
      });

      setNotifyPermission([...new Set(permissionsArray)]);
    } catch (error) {
      console.error('Error setting notification permissions:', error);
    }
  }, [permissions, my_user]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  /**
   * Lista de notificações filtradas e memoizadas
   */
  const filteredNotifications = useMemo(
    () => checkNotifyPermission(),
    [checkNotifyPermission]
  );

  /**
   * Conteúdo de notificações renderizado
   */
  const notificationContent = useMemo(() => {
    if (!filteredNotifications || filteredNotifications.length === 0) {
      return <EmptyNotificationState />;
    }

    return (
      <Box sx={{ px: 2, pb: 8 }}>
        {filteredNotifications.map((item, index) => (
          <Fade
            key={item.id || index}
            in={isOpenNotify}
            timeout={300}
            style={{ transitionDelay: `${index * 50}ms` }}
          >
            <NotificationCard
              onClick={() => customCall(item.type, item.call, item)}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  customCall(item.type, item.call, item);
                }
              }}
            >
              {ReactHtmlParser(item.message)}
            </NotificationCard>
          </Fade>
        ))}
      </Box>
    );
  }, [filteredNotifications, isOpenNotify, customCall]);

  /**
   * Ícone de notificação com badge
   */
  const notificationIcon = useMemo(() => (
    <IconButton
      onClick={toggleOpenNotify}
      aria-label="Notificações"
      sx={{ color: SECONDARY_COLOR }}
    >
      {newNotify.status && newNotify.quantity > 0 ? (
        <NotificationBadge badgeContent={newNotify.quantity} color="error">
          <FontAwesomeIcon icon={icon.faBell} />
        </NotificationBadge>
      ) : (
        <FontAwesomeIcon icon={icon.faBell} />
      )}
    </IconButton>
  ), [toggleOpenNotify, newNotify]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      {/* Botão Flutuante para Abrir o Menu (opcional - use se não tiver botão externo) */}
      {!isOpen && !isOpenNotify && (
        <Fade in={!isOpen} timeout={300}>
          <FloatingMenuButton
            onClick={toggleOpen}
            aria-label="Abrir menu"
            size="large"
          >
            <MenuIcon />
          </FloatingMenuButton>
        </Fade>
      )}

      {/* Menu Principal */}
      <StyledDrawer
        anchor="right"
        open={isOpen}
        onClose={resetNavs}
        variant="temporary"
        ModalProps={{
          keepMounted: true, // Melhora a performance em mobile
          BackdropProps: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.8)', // Fundo escuro semi-transparente
            },
          },
        }}
        SlideProps={{
          timeout: 500,
        }}
      >
        <MenuContainer>
          {/* AJUSTE: Botão muda de ícone hamburguer para X quando menu está aberto */}
          <CloseButtonContainer>
            {!isOpenNotify && (
              <Fade in={isOpen} timeout={300}>
                <MenuButton
                  onClick={toggleOpen}
                  aria-label="Fechar menu"
                  size="large"
                >
                  <CloseIcon />
                </MenuButton>
              </Fade>
            )}
          </CloseButtonContainer>

          {/* Seção do Avatar e Nome do Usuário */}
          <Slide
            direction="down"
            in={isOpen}
            timeout={400}
            mountOnEnter
            unmountOnExit
          >
            <AvatarSection>
              {my_user?.given_name && (
                <>
                  {/* PASSADO COMPONENTE PARA TROCAR AVATAR */}
                  <AvatarManager onCloseMenu={resetNavs} />
                </>
              )}
            </AvatarSection>
          </Slide>

          {/* Botão Minha Conta */}
          <Fade in={isOpen} timeout={500}>
            <MyAccountButton
              onClick={handleMyAccountClick}
              role="button"
              tabIndex={0}
              onKeyPress={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleMyAccountClick();
                }
              }}
            >
              <SettingsIcon />
              <Typography>Minha conta</Typography>
            </MyAccountButton>
          </Fade>

          {/* Área de Conteúdo Scrollável */}
          <ContentArea>
            {/* Componente de Navegação com todos os itens do menu */}
            <Fade in={isOpen} timeout={600}>
              <Box>
                <MenuNavigation
                  hasParent={hasParent}
                  onNavigate={(path) => {
                    navigate(path);
                    resetNavs();
                  }}
                  onToggleNotify={() => {
                    toggleOpenNotify();
                    setNewNotify({ status: false, quantity: newNotify.quantity });
                    if (!isOpen) {
                      toggleOpen();
                    }
                  }}
                  onTourGuide={() => {
                    setOpenModalStart?.(true);
                    resetNavs();
                  }}
                  onLogout={handleLogout}
                  notifyCount={newNotify.quantity}
                  t={t}
                />
              </Box>
            </Fade>

            {/* Ícones Sociais */}
            {isOpen && (
              <Fade in={isOpen} timeout={800}>
                <Box>
                  <SocialIcons />
                </Box>
              </Fade>
            )}
          </ContentArea>
        </MenuContainer>
      </StyledDrawer>

      {/* Menu de Notificações */}
      {notification && (
        <NotificationDrawer
          anchor="right"
          open={isOpenNotify}
          onClose={toggleOpenNotify}
          variant="temporary"
          ModalProps={{
            keepMounted: true, // Melhora a performance em mobile
            BackdropProps: {
              sx: {
                backgroundColor: 'rgba(0, 0, 0, 0.8)', // Fundo escuro semi-transparente
              },
            },
          }}
          SlideProps={{
            timeout: 500,
          }}
        >
          <MenuContainer>
            {/* Conteúdo das Notificações */}
            <ContentArea sx={{ pt: 2 }}>
              <Fade in={isOpenNotify} timeout={400}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      color: PRIMARY_COLOR,
                      mb: 2,
                      fontWeight: 'bold',
                    }}
                  >
                    Notificações
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {notificationContent}

                  {/* Botão Voltar integrado ao conteúdo */}
                  <Box sx={{ px: 2, py: 3, mt: 2 }}>
                    <Fade in={isOpenNotify} timeout={500}>
                      <ActionButton
                        variant="secondary"
                        onClick={toggleOpenNotify}
                        role="button"
                        tabIndex={0}
                        sx={{ width: '100%' }}
                      >
                        <Typography sx={{ color: SECONDARY_COLOR }}>Voltar</Typography>
                      </ActionButton>
                    </Fade>
                  </Box>
                </Box>
              </Fade>
            </ContentArea>
          </MenuContainer>
        </NotificationDrawer>
      )}
    </>
  );
};

export default MotionMenu;